/**
 * @module read-only/readonlyediting
 */

import {
    Plugin,
    Widget,
    toWidget,
    viewToModelPositionOutsideModelElement
} from 'ckeditor5';

import ReadOnlyCommand from './readonlycommand';

/**
 * The read-only editing feature.
 *
 * * It introduces the `readonly` text attribute that is rendered as
 * a `<span>` element with the `readonly` CSS class.
 * * It registers the `'readonly'` command.
 *
 * @extends module:core/plugin~Plugin
 */
export default class ReadOnlyEditing extends Plugin {
    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'ReadOnlyEditing';
    }

    /**
     * @inheritDoc
     */
    static get requires() {
        return [Widget];
    }

    /**
     * @inheritDoc
     */
    init() {
        const editor = this.editor;

        editor.model.schema.register('readonly', {
            allowWhere: '$text',
            isInline: true,
            isObject: true,
            allowAttributesOf: '$text',
            allowAttributes: ['content']
        });

        this._defineConverters();

        editor.commands.add('readonly', new ReadOnlyCommand(editor));

        this.editor.editing.mapper.on(
            'viewToModelPosition',
            viewToModelPositionOutsideModelElement(this.editor.model, viewElement => viewElement.hasClass('readonly'))
        );

        /*
        editor.model.schema.extend( '$text', { allowAttributes: [ 'readonly' ] } );

        editor.conversion.for( 'upcast' ).elementToAttribute( {
            model: 'readonly',
            view: {
                name: 'span',
                classes: 'readonly'
            }
        } );

        editor.conversion.for( 'downcast' ).attributeToElement( {
            model: 'readonly',
            view: ( modelAttributeValue, { writer } ) => {
                if ( modelAttributeValue ) {
                    // Make the readonly <span> outer-most in the view.
                    const viewElement = writer.createAttributeElement( 'span',
                        { class: 'readonly ck-widget', contenteditable: 'false', draggable: 'true' },
                        { priority: -10 }
                    );
                    return viewElement;
                }
            }
        } );
        */
    }

    _defineConverters() {
        const conversion = this.editor.conversion;

        conversion.for('upcast').elementToElement({
            view: {
                name: 'span',
                classes: ['readonly']
            },
            model: (viewElement, { writer: modelWriter }) => {
                const content = viewElement.getChild(0).data;
                return modelWriter.createElement('readonly', { content });
            }
        });

        conversion.for('editingDowncast').elementToElement({
            model: 'readonly',
            view: (modelItem, { writer: viewWriter }) => {
                const widgetElement = createPlaceholderView(modelItem, viewWriter);
                return toWidget(widgetElement, viewWriter);
            }
        });

        conversion.for('dataDowncast').elementToElement({
            model: 'readonly',
            view: (modelItem, { writer: viewWriter }) => createPlaceholderView(modelItem, viewWriter)
        });

        function createPlaceholderView(modelItem, viewWriter) {
            const content = modelItem.getAttribute('content');

            const readonlyView = viewWriter.createContainerElement(
                'span',
                { class: 'readonly' },
                { isAllowedInsideAttributeElement: true }
            );

            const innerText = viewWriter.createText(content);
            viewWriter.insert(viewWriter.createPositionAt(readonlyView, 0), innerText);

            return readonlyView;
        }
    }
}
